import { HttpClient } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  clearPkceParams,
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  getPkceParams,
  storePkceParams,
} from '../utils/pkce.utils';

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

/**
 * PKCE + BFF auth flow. Access token in-memory only; refresh lives in an HttpOnly
 * cookie owned by the BFF and is never exposed to JS.
 */
@Service()
export class ApiAuthService {
  private readonly http = inject(HttpClient);

  private readonly accessTokenSignal = signal<string | null>(null);
  private readonly tokenExpirySignal = signal<number | null>(null);
  // ponytail: dedup concurrent refresh calls (F3) — N 401s share one refresh
  // round-trip. Ceiling: a refresh that fails still rejects all waiters, which
  // is correct; a second refresh attempt after failure starts a new request.
  private refreshInFlight$: Observable<TokenResponse> | null = null;

  readonly isAuthenticated = computed(() => this.accessTokenSignal() !== null);

  private readonly clientId = 'jitsupedia-spa';
  private readonly authorizeUrl = `${environment.api.baseUrl}/connect/authorize`;
  private readonly exchangeUrl = `${environment.api.baseUrl}/bff/auth/token/exchange`;
  private readonly refreshUrl = `${environment.api.baseUrl}/bff/auth/token/refresh`;
  private readonly logoutUrl = `${environment.api.baseUrl}/bff/auth/logout`;
  private readonly redirectUri =
    typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '';
  private readonly scope = 'openid profile api offline_access';

  getAccessToken(): string | null {
    return this.accessTokenSignal();
  }

  /** True if there is no access token or it is within 30s of expiry. */
  isTokenExpired(): boolean {
    const expiry = this.tokenExpirySignal();
    if (!expiry) return true;
    return Date.now() >= expiry - 30_000;
  }

  async startPkceFlow(): Promise<void> {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    storePkceParams(codeVerifier, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${this.authorizeUrl}?${params.toString()}`;
  }

  exchangeCodeForTokens(code: string, returnedState: string): Observable<TokenResponse> {
    const { verifier, state: storedState } = getPkceParams();

    if (!storedState || storedState !== returnedState) {
      return throwError(() => new Error('State mismatch - possible CSRF attack'));
    }
    if (!verifier) {
      return throwError(() => new Error('Code verifier not found'));
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.clientId,
      code,
      redirect_uri: this.redirectUri,
      code_verifier: verifier,
    });

    return this.http
      .post<TokenResponse>(this.exchangeUrl, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          this.storeToken(response);
          clearPkceParams();
        }),
        catchError((error) => {
          console.error('Token exchange failed:', error);
          clearPkceParams();
          return throwError(() => error);
        }),
      );
  }

  refreshToken(): Observable<TokenResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .post<TokenResponse>(this.refreshUrl, null, { withCredentials: true })
      .pipe(
        tap((response) => this.storeToken(response)),
        catchError((error) => {
          this.clearInMemoryToken();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshInFlight$;
  }

  clearToken(): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.http.post<void>(this.logoutUrl, null, { withCredentials: true }).subscribe({
        next: () => {
          this.clearInMemoryToken();
          subscriber.next();
          subscriber.complete();
        },
        error: () => {
          this.clearInMemoryToken();
          subscriber.next();
          subscriber.complete();
        },
      });
    });
  }

  async ensureAuthenticated(): Promise<string | null> {
    const token = this.accessTokenSignal();
    if (token && !this.isTokenExpired()) {
      return token;
    }
    try {
      const response = await firstValueFrom(this.refreshToken());
      return response.access_token;
    } catch {
      await this.startPkceFlow();
      return null;
    }
  }

  async tryRestoreSession(): Promise<boolean> {
    try {
      await firstValueFrom(this.refreshToken());
      return true;
    } catch {
      return false;
    }
  }

  private storeToken(response: TokenResponse): void {
    this.accessTokenSignal.set(response.access_token);
    this.tokenExpirySignal.set(Date.now() + response.expires_in * 1000);
  }

  private clearInMemoryToken(): void {
    this.accessTokenSignal.set(null);
    this.tokenExpirySignal.set(null);
  }
}
